import {
  Badge,
  Card,
  ContentSection,
  CountBadge,
  DirectionalIcon,
  Typography
} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {useTranslation} from 'react-i18next';

import {BuyerAvatar} from '@/features/buyers/components';
import {formatBuyerDisplayName, getBuyerIdNumber} from '@/features/buyers/utils';

import type {BuyerDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface PurchaseBuyersSectionProps {
  buyers: BuyerDTO[];
  currentBuyerId: string;
  onViewBuyer: (buyerId: string) => void;
}

export const PurchaseBuyersSection: FC<PurchaseBuyersSectionProps> = ({
  buyers,
  currentBuyerId,
  onViewBuyer
}) => {
  const {t} = useTranslation('trustAccounts');

  // Stable order regardless of who's "current" — id tiebreaks createdAt ties from bulk-created buyers.
  const sortedBuyers = [...buyers].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)
  );

  return (
    <ContentSection
      title={t('view.buyers.detail.purchaseBuyers.title')}
      titleBadge={<CountBadge count={buyers.length} />}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sortedBuyers.map(buyer => (
          <PurchaseBuyerCard
            key={buyer.id}
            buyer={buyer}
            isCurrent={buyer.id === currentBuyerId}
            onViewBuyer={onViewBuyer}
          />
        ))}
      </div>
    </ContentSection>
  );
};

interface PurchaseBuyerCardProps {
  buyer: BuyerDTO;
  isCurrent: boolean;
  onViewBuyer: (buyerId: string) => void;
}

const PurchaseBuyerCard: FC<PurchaseBuyerCardProps> = ({buyer, isCurrent, onViewBuyer}) => {
  const {t, i18n} = useTranslation('trustAccounts');
  const displayName = formatBuyerDisplayName(buyer.fullName);
  const idNumber = getBuyerIdNumber(buyer, i18n.language);

  return (
    <Card
      onCardClick={isCurrent ? undefined : () => onViewBuyer(buyer.id)}
      className={cn('gap-3 p-4', isCurrent && 'choice-selected hover:shadow-xs')}
      aria-label={isCurrent ? undefined : displayName}
    >
      <div className="flex min-w-0 items-center gap-3">
        <BuyerAvatar buyer={buyer} />
        <div className="flex min-w-0 flex-col gap-0.5 text-start">
          <Typography size="sm" weight="medium" className="truncate">
            {displayName}
          </Typography>
          <Typography size="xs" color="tertiary" className="tabular-nums">
            {idNumber ?? '—'}
          </Typography>
        </div>
        {isCurrent ? (
          <Badge size="sm" className="ms-auto shrink-0">
            {t('view.buyers.detail.purchaseBuyers.current')}
          </Badge>
        ) : (
          <DirectionalIcon
            name="chevron-forward"
            className="ms-auto size-4 shrink-0 text-fg-quaternary"
          />
        )}
      </div>
    </Card>
  );
};
